import { useEffect, useMemo, useState, type ReactNode } from "react"
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Coffee,
  ExternalLink,
  MapPin,
  UtensilsCrossed,
  Users,
  X,
} from "lucide-react"
import type { HotelOption } from "@/lib/api/types"
import { formatPriceDelta } from "@/lib/booking"
import { Button } from "@/components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
  type CarouselApi,
} from "@/components/ui/carousel"
import { Dialog, DialogContent } from "@/components/ui/dialog"

interface HotelCardProps {
  hotel: HotelOption
  visuallySelected: boolean
  isIncludedInPackage: boolean
  currency: string
  selectedPriceDiffPerPerson: number
  onSelect: () => void
  onOpenBookingRequest?: () => void
}

export function HotelCard({
  hotel,
  visuallySelected,
  isIncludedInPackage,
  currency,
  selectedPriceDiffPerPerson,
  onSelect,
  onOpenBookingRequest,
}: HotelCardProps) {
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const images = useMemo(
    () => (hotel.images.length > 0 ? hotel.images : []),
    [hotel.images]
  )

  let priceLabel: string
  let showPrice: boolean

  if (hotel.soldOut) {
    priceLabel = ""
    showPrice = false
  } else if (isIncludedInPackage) {
    priceLabel = "Included in package"
    showPrice = true
  } else if (visuallySelected) {
    showPrice = false
    priceLabel = ""
  } else {
    const diff =
      selectedPriceDiffPerPerson === 0
        ? hotel.priceDiffPerPerson
        : hotel.priceDiffPerPerson - selectedPriceDiffPerPerson
    priceLabel = formatPriceDelta(diff, currency, true)
    showPrice = true
  }

  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-[18px] border transition-all @min-[48rem]:flex-row ${
        visuallySelected
          ? "border-primary/60"
          : "border-border/90 hover:border-border"
      }`}
    >
      <div className="relative h-52 w-full shrink-0 overflow-hidden @min-[48rem]:h-auto @min-[48rem]:w-[320px]">
        {images.length > 0 ? (
          <HotelGallery
            images={images}
            hotelName={hotel.name}
            currentIndex={imageIndex}
            onIndexChange={setImageIndex}
            onOpenFullscreen={() => setGalleryOpen(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-secondary text-center text-sm text-muted-foreground">
            Images will be available soon
          </div>
        )}
      </div>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent
          className="fixed inset-0 z-50 h-screen w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 bg-black/90 p-0 @min-[40rem]:inset-0 @min-[40rem]:h-screen @min-[40rem]:w-screen @min-[40rem]:max-w-none @min-[40rem]:translate-x-0 @min-[40rem]:translate-y-0 @min-[40rem]:rounded-none @min-[40rem]:p-0 @min-[64rem]:bg-black/80"
          showCloseButton={false}
        >
          <div
            className="absolute inset-0"
            onClick={() => setGalleryOpen(false)}
          />
          <div className="relative flex h-full min-h-0 flex-col p-2 @min-[40rem]:p-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setGalleryOpen(false)}
              className="absolute top-3 right-3 z-10 size-10 rounded-full bg-black/40 text-white/70 backdrop-blur-sm hover:bg-black/60 hover:text-white"
            >
              <X className="size-5" />
            </Button>

            <HotelFullscreenGallery
              images={images}
              hotelId={hotel.hotelId}
              hotelName={hotel.name}
              startIndex={imageIndex}
              onIndexChange={setImageIndex}
            />
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-1 flex-col justify-between gap-4 p-5 @min-[48rem]:p-6">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-[22px] leading-none font-bold text-foreground">
              {hotel.name}
            </h3>

            <div className="mt-2 flex gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <span
                  key={index}
                  className={`text-base ${
                    index < (hotel.stars ?? 0)
                      ? "text-amber-400"
                      : "text-foreground/20"
                  }`}
                >
                  ★
                </span>
              ))}
            </div>
            {hotel.googleReviewsUrl && (
              <a
                href={hotel.googleReviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-amber-400 transition-colors hover:text-amber-400/80"
              >
                Google Reviews
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>

          <p className="text-[15px] leading-6 text-foreground/85">
            {hotel.description || "Comfortable stay close to the event venue."}
          </p>

          <div className="flex flex-wrap gap-2">
            <MetaPill>{hotel.roomCapacity}-person room</MetaPill>
            {hotel.mealPlan === "BREAKFAST_INCLUDED" && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-700/30 bg-green-600/10 px-3 py-1.5 text-xs font-medium text-green-700 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-400">
                <Coffee className="size-3.5" />
                Breakfast included
              </span>
            )}
            {hotel.mealPlan === "NO_MEAL" && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-transparent px-3 py-1.5 text-xs font-medium text-muted-foreground">
                <UtensilsCrossed className="size-3.5" />
                No meal
              </span>
            )}
            {hotel.shared && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 px-3 py-1.5 text-xs font-medium text-blue-500"
              >
                Shared room
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[15px] font-medium text-foreground">
              <MapPin className="size-4 text-muted-foreground" />
              <span>
                {hotel.distanceFromVenue || "Venue distance on request"}
              </span>
            </div>

            <div className="flex items-center gap-2 text-[15px] text-blue-500">
              <Users className="size-4 text-blue-500" />
              {hotel.soldOut ? (
                <Button
                  type="button"
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenBookingRequest?.()
                  }}
                  className="h-auto p-0 text-[13px] text-blue-500 underline decoration-blue-500/40 underline-offset-2 hover:text-blue-500/80"
                >
                  Sold out — interested? Tell us
                </Button>
              ) : (
                <span>Only {hotel.availableRooms} rooms left</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end justify-between gap-4 @min-[40rem]:flex-row @min-[40rem]:items-end">
          <div className="min-h-11 flex-1 @min-[40rem]:block hidden" />

          <div className="flex w-full flex-col items-end gap-3 @min-[40rem]:w-auto">
            {showPrice && (
              <div className="text-end">
                <span
                  className={`leading-none ${
                    hotel.soldOut
                      ? "text-[20px] font-bold text-muted-foreground"
                      : isIncludedInPackage
                        ? "text-[15px] font-medium text-foreground"
                        : "text-[26px] font-bold text-primary @min-[48rem]:text-[32px]"
                  }`}
                >
                  {priceLabel}
                </span>
              </div>
            )}

            <Button
              onClick={hotel.soldOut ? onOpenBookingRequest : onSelect}
              variant={hotel.soldOut ? "destructive" : visuallySelected ? "outline" : "default"}
              size="xl"
              type="button"
              className="w-full @min-[40rem]:w-auto"
            >
              {visuallySelected && <Check className="mr-2 size-5" />}
              {hotel.soldOut
                ? "Sold Out"
                : visuallySelected
                  ? "Selected"
                  : "Select Hotel"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface HotelGalleryProps {
  images: string[]
  hotelName: string | null
  currentIndex: number
  onIndexChange: (index: number) => void
  onOpenFullscreen: () => void
}

/**
 * Inline gallery inside the hotel card. Uses shadcn Carousel (Embla) with
 * looping and swipe gestures. Tapping a slide opens the full-screen viewer.
 */
function HotelGallery({
  images,
  hotelName,
  currentIndex,
  onIndexChange,
  onOpenFullscreen,
}: HotelGalleryProps) {
  const [api, setApi] = useState<CarouselApi>()

  // Sync external index → carousel
  useEffect(() => {
    if (!api) return
    if (api.selectedScrollSnap() !== currentIndex) {
      api.scrollTo(currentIndex)
    }
  }, [api, currentIndex])

  // Sync carousel → external index
  useEffect(() => {
    if (!api) return
    const handleSelect = () => onIndexChange(api.selectedScrollSnap())
    api.on("select", handleSelect)
    return () => {
      api.off("select", handleSelect)
    }
  }, [api, onIndexChange])

  return (
    <Carousel
      setApi={setApi}
      opts={{ loop: images.length > 1 }}
      className="h-full [&_[data-slot=carousel-content]]:h-full"
    >
      <CarouselContent className="ml-0 h-full">
        {images.map((src, idx) => (
          <CarouselItem
            key={`${hotelName ?? "hotel"}-${idx}`}
            className="relative h-full pl-0"
          >
            <Button
              type="button"
              variant="ghost"
              onClick={onOpenFullscreen}
              className="h-full w-full cursor-pointer rounded-none p-0"
              aria-label={`Open ${hotelName ?? "hotel"} gallery`}
            >
              <img
                src={src}
                alt={`${hotelName ?? "Hotel"} photo ${idx + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </Button>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-r from-transparent via-black/10 to-black/45" />
          </CarouselItem>
        ))}
      </CarouselContent>

      {images.length > 1 && (
        <>
          <GalleryNavButton direction="prev" />
          <GalleryNavButton direction="next" />
        </>
      )}
    </Carousel>
  )
}

/**
 * Custom nav button that overlays the carousel image. We don't use the
 * default `CarouselPrevious`/`CarouselNext` because they're positioned
 * outside the carousel container.
 */
function GalleryNavButton({ direction }: { direction: "prev" | "next" }) {
  const { scrollPrev, scrollNext } = useCarousel()
  const isPrev = direction === "prev"

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={(event) => {
        event.stopPropagation()
        if (isPrev) scrollPrev()
        else scrollNext()
      }}
      className={`absolute top-1/2 z-10 size-10 -translate-y-1/2 rounded-full bg-black/25 text-white/70 backdrop-blur-sm hover:bg-black/40 hover:text-white ${
        isPrev ? "left-4" : "right-4"
      }`}
      aria-label={isPrev ? "Previous image" : "Next image"}
    >
      {isPrev ? <ChevronLeft className="size-5" /> : <ChevronRight className="size-5" />}
    </Button>
  )
}

interface HotelFullscreenGalleryProps {
  images: string[]
  hotelId: string
  hotelName: string | null
  startIndex: number
  onIndexChange: (index: number) => void
}

/**
 * Full-screen gallery shown in a Dialog. Uses Carousel for the main image
 * with a thumbnail strip below that stays in sync via the Embla API.
 */
function HotelFullscreenGallery({
  images,
  hotelId,
  hotelName,
  startIndex,
  onIndexChange,
}: HotelFullscreenGalleryProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [activeIndex, setActiveIndex] = useState(startIndex)

  useEffect(() => {
    if (!api) return
    const handleSelect = () => {
      const next = api.selectedScrollSnap()
      setActiveIndex(next)
      onIndexChange(next)
    }
    api.on("select", handleSelect)
    return () => {
      api.off("select", handleSelect)
    }
  }, [api, onIndexChange])

  return (
    <>
      <Carousel
        setApi={setApi}
        opts={{ loop: images.length > 1, startIndex }}
        className="relative min-h-0 flex-1"
      >
        <CarouselContent className="ml-0 h-full">
          {images.map((src, idx) => (
            <CarouselItem
              key={`${hotelId}-fs-${idx}`}
              className="flex items-center justify-center pl-0"
            >
              <img
                src={src}
                alt={`${hotelName ?? "Hotel"} photo ${idx + 1}`}
                className="h-full w-full object-contain"
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {images.length > 1 && (
          <>
            <FullscreenNavButton direction="prev" />
            <FullscreenNavButton direction="next" />
          </>
        )}
      </Carousel>

      {images.length > 1 && (
        <div className="mt-3 flex shrink-0 justify-center gap-2 overflow-x-auto pb-1">
          {images.map((image, idx) => (
            <Button
              key={`${hotelId}-thumb-${idx}`}
              type="button"
              variant="ghost"
              onClick={() => api?.scrollTo(idx)}
              className={`h-auto overflow-hidden rounded-lg border p-0 transition-all ${
                idx === activeIndex ? "border-primary" : "border-border/40"
              }`}
            >
              <img
                src={image}
                alt={`${hotelName ?? "Hotel"} thumbnail ${idx + 1}`}
                className="h-16 w-24 object-cover"
              />
            </Button>
          ))}
        </div>
      )}
    </>
  )
}

function FullscreenNavButton({ direction }: { direction: "prev" | "next" }) {
  const { scrollPrev, scrollNext } = useCarousel()
  const isPrev = direction === "prev"

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={() => (isPrev ? scrollPrev() : scrollNext())}
      className={`absolute top-1/2 z-10 size-11 -translate-y-1/2 rounded-full bg-black/40 text-white/80 backdrop-blur-sm hover:bg-black/60 hover:text-white ${
        isPrev ? "left-3" : "right-3"
      }`}
      aria-label={isPrev ? "Previous image" : "Next image"}
    >
      {isPrev ? <ChevronLeft className="size-6" /> : <ChevronRight className="size-6" />}
    </Button>
  )
}

function MetaPill({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-transparent px-3 py-1.5 text-xs font-medium text-foreground/80"
    >
      {children}
    </span>
  )
}
